/**
 * airlogi-bridge (Chrome 拡張機能) との通信ラッパー。
 *
 * 拡張は Wonect 管理者の Chrome にインストールされている前提。
 * chrome.runtime.sendMessage(EXTENSION_ID, ...) で各 action を呼び出す。
 *
 * 拡張 ID は backend の /api/v1/admin/airlogi/extension-id から取得する。
 */

// ---- 型定義 (拡張の src/lib/messages.ts と揃える) ----

export type AirlogiAction =
  | "airlogi.getSessionStatus"
  | "airlogi.getProductMaster"
  | "airlogi.getStockInfo"
  | "airlogi.uploadShippingCsv";

export interface AirlogiSuccess<D = unknown> {
  ok: true;
  data: D;
}

export interface AirlogiFailure {
  ok: false;
  error: AirlogiErrorCode;
  message: string;
  details?: unknown;
}

export type AirlogiResponse<D = unknown> = AirlogiSuccess<D> | AirlogiFailure;

export type AirlogiErrorCode =
  | "SESSION_EXPIRED"
  | "NETWORK_ERROR"
  | "PARSE_ERROR"
  | "UNKNOWN_ACTION"
  | "INVALID_PARAMS"
  | "INTERNAL_ERROR"
  | "EXTENSION_NOT_INSTALLED"
  | "EXTENSION_NOT_CONFIGURED";

export interface SessionStatus {
  loggedIn: boolean;
  accountId?: string;
  checkedAt: string;
}

export interface StockInfoRow {
  jan: string;
  quantity: number;
  location?: string;
  // Air-Logi の生フィールドも入る（品名 / 総在庫数 / etc.）
  [key: string]: string | number | undefined;
}

export interface StockInfoData {
  rows: StockInfoRow[];
  fetchedAt: string;
}

// chrome.runtime の型を Window から取り出す
type ChromeRuntime = {
  sendMessage: (
    extensionId: string,
    message: unknown,
    callback?: (response: unknown) => void,
  ) => void;
  lastError?: { message?: string };
};

declare global {
  interface Window {
    chrome?: { runtime?: ChromeRuntime };
  }
}

// ---- ラッパー ----

const CHROME_RUNTIME_TIMEOUT_MS = 120_000; // CSV 取得は重い (42213 件 × 21 列)

/**
 * 拡張に対して 1 回 sendMessage を投げ、AirlogiResponse を Promise で返す。
 *
 * 拡張がインストールされていない / 起動していない場合は EXTENSION_NOT_INSTALLED を返す。
 */
export function callBridge<D = unknown>(
  extensionId: string,
  action: AirlogiAction,
  params?: unknown,
): Promise<AirlogiResponse<D>> {
  if (!extensionId) {
    return Promise.resolve({
      ok: false,
      error: "EXTENSION_NOT_CONFIGURED",
      message: "拡張 ID が設定されていません",
    });
  }
  const chrome = window.chrome?.runtime;
  if (!chrome || !chrome.sendMessage) {
    return Promise.resolve({
      ok: false,
      error: "EXTENSION_NOT_INSTALLED",
      message:
        "Chrome 拡張 airlogi-bridge にアクセスできません。インストール後にこのページを再読み込みしてください。",
    });
  }

  return new Promise((resolve) => {
    const timeoutId = window.setTimeout(() => {
      resolve({
        ok: false,
        error: "NETWORK_ERROR",
        message: `拡張からの応答が ${CHROME_RUNTIME_TIMEOUT_MS / 1000} 秒以内に返ってきませんでした`,
      });
    }, CHROME_RUNTIME_TIMEOUT_MS);

    try {
      chrome.sendMessage(
        extensionId,
        { action, params, requestId: crypto.randomUUID() },
        (response: unknown) => {
          window.clearTimeout(timeoutId);
          if (chrome.lastError) {
            resolve({
              ok: false,
              error: "EXTENSION_NOT_INSTALLED",
              message:
                chrome.lastError.message ||
                "拡張との通信に失敗しました。拡張がインストール・有効化されているか確認してください。",
            });
            return;
          }
          if (!response || typeof response !== "object") {
            resolve({
              ok: false,
              error: "INTERNAL_ERROR",
              message: "拡張から不正なレスポンスが返りました",
              details: response,
            });
            return;
          }
          resolve(response as AirlogiResponse<D>);
        },
      );
    } catch (e: unknown) {
      window.clearTimeout(timeoutId);
      resolve({
        ok: false,
        error: "INTERNAL_ERROR",
        message: e instanceof Error ? e.message : String(e),
      });
    }
  });
}

// ---- 高レベル API ----

export async function fetchSessionStatus(
  extensionId: string,
): Promise<AirlogiResponse<SessionStatus>> {
  return await callBridge<SessionStatus>(extensionId, "airlogi.getSessionStatus");
}

export async function fetchStockInfo(
  extensionId: string,
): Promise<AirlogiResponse<StockInfoData>> {
  return await callBridge<StockInfoData>(extensionId, "airlogi.getStockInfo");
}

// ---- エラー → 日本語メッセージ ----

export function bridgeErrorToMessage(failure: AirlogiFailure): string {
  switch (failure.error) {
    case "SESSION_EXPIRED":
      return "Air-Logi のログインが切れています。Air-Logi タブを開いて再ログインしてください。";
    case "NETWORK_ERROR":
      return `Air-Logi との通信に失敗しました: ${failure.message}`;
    case "PARSE_ERROR":
      return `Air-Logi のレスポンス解析に失敗しました: ${failure.message}`;
    case "EXTENSION_NOT_INSTALLED":
      return failure.message;
    case "EXTENSION_NOT_CONFIGURED":
      return "管理者向け設定で airlogi-bridge の拡張 ID が未設定です。";
    case "UNKNOWN_ACTION":
    case "INVALID_PARAMS":
    case "INTERNAL_ERROR":
    default:
      return `airlogi-bridge エラー: ${failure.message}`;
  }
}
